import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";
import CreditAuthorizationsApiClient from "@/api/creditAuthorizationsApiClient";
import { CreditAuthorizationUpdateDTO } from "@/types/CreditAuthorizationUpdateDTO";
import { useFieldSchemaV2FactoryContext, FieldFormV2Module } from "@ucl/library";
import { createCreditAuthorizationFormBuilder } from "@/builders/creditAuthorizationFormBuilder";
import { useAutoSave } from "./useAutoSave";

export interface UseCreditAuthorizationFormResult {
  formValue: CreditAuthorizationUpdateDTO | null;
  setFormValue: (newValue: CreditAuthorizationUpdateDTO, newFieldValue?: any) => void;
  saveFormData: (value: CreditAuthorizationUpdateDTO) => Promise<void>;
  schemaFactory: any;
  formBuilder: any;
  fieldFormModule: React.RefObject<FieldFormV2Module<CreditAuthorizationUpdateDTO> | null>;
  loading: boolean;
  error: string | null;
  isSafeToCloseBrowserTab: boolean;
}

export const useCreditAuthorizationForm = (
  creditAuthorizationId: string
): UseCreditAuthorizationFormResult => {
  const [formValue, setFormValue] = useState<CreditAuthorizationUpdateDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSafeToCloseBrowserTab, setIsSafeToCloseBrowserTab] = useState<boolean>(false);

  const creditAuthorizationsApiClient = useAuthenticatedApiClient(CreditAuthorizationsApiClient);

  const fieldFormModule = useRef<FieldFormV2Module<CreditAuthorizationUpdateDTO> | null>(null);

  const { schemaFactory } = useFieldSchemaV2FactoryContext();

  const formBuilder = useMemo(() => {
    if (!schemaFactory) return undefined;
    return createCreditAuthorizationFormBuilder(schemaFactory);
  }, [schemaFactory]);

  // Use the generic auto-save hook
  const { markDirty } = useAutoSave({
    saveFunction: async () => {
      if (formValue) {
        await fieldFormModule.current?.submit();
      }
    },
    debounceDelay: 1000,
  });

  // Fetch the current credit authorization
  useEffect(() => {
    const fetchCreditAuthorization = async () => {
      try {
        setLoading(true);
        setError(null);
        const response =
          await creditAuthorizationsApiClient.getCreditAuthorizationById(creditAuthorizationId);
        setFormValue(response);
      } catch (err) {
        console.error("Failed to fetch credit authorization:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch credit authorization");
      } finally {
        setLoading(false);
      }
    };

    fetchCreditAuthorization();
  }, [creditAuthorizationId, creditAuthorizationsApiClient]);

  const setFormValueWithAutoSave = useCallback(
    async (newValue: CreditAuthorizationUpdateDTO, _newFieldValue?: any) => {
      setFormValue(newValue);
      // Mark as dirty and trigger auto-save
      markDirty();
    },
    [markDirty]
  );

  const saveFormData = useCallback(
    async (value: CreditAuthorizationUpdateDTO) => {
      try {
        setError(null);

        const response = await creditAuthorizationsApiClient.updateCreditAuthorization(
          creditAuthorizationId,
          value
        );
        setFormValue(response);
        
        // Set flag to indicate it's safe to close the browser tab
        setIsSafeToCloseBrowserTab(true);
      } catch (err) {
        console.error("Failed to update credit authorization:", err);
        setError(err instanceof Error ? err.message : "Failed to update credit authorization");
        throw err;
      }
    },
    [creditAuthorizationId, creditAuthorizationsApiClient]
  );

  return {
    formValue,
    setFormValue: setFormValueWithAutoSave,
    saveFormData,
    schemaFactory,
    formBuilder,
    fieldFormModule,
    loading,
    error,
    isSafeToCloseBrowserTab,
  };
};
